import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  Observable,
  catchError,
  concatMap,
  defer,
  finalize,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import {
  ApiCacheEntry,
  buildCacheKey,
  isCacheableRequest,
  isMutatingRequest,
  isOffline,
} from '../http/api-cache.util';
import { ApiCacheService } from '../services/api-cache.service';
import { AuthService } from '../services/auth.service';

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  const cache = inject(ApiCacheService);

  if (isMutatingRequest(req)) {
    return next(req).pipe(
      concatMap((event) => {
        if (!(event instanceof HttpResponse) || !event.ok) {
          return of(event);
        }

        return from(cache.invalidateByRequest(req)).pipe(map(() => event));
      }),
    );
  }

  if (!isCacheableRequest(req)) {
    return next(req);
  }

  const key = buildCacheKey(req, cacheUserKey(inject(AuthService)));
  const pending = cache.getInFlight(key);
  if (pending) {
    return pending;
  }

  const request$ = defer(() => readCache(cache, key)).pipe(
    switchMap((cached) => serveCacheable(req, next, cache, key, cached)),
    finalize(() => cache.clearInFlight(key)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  cache.setInFlight(key, request$);
  return request$;
};

function serveCacheable(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  cache: ApiCacheService,
  key: string,
  cached: ApiCacheEntry | null,
): Observable<HttpEvent<unknown>> {
  if (cached && (cache.isFresh(cached) || isOffline())) {
    return of(toHttpResponse(req, cached));
  }

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && event.ok) {
        void cache.putResponse(key, req, event);
      }
    }),
    catchError((error: unknown) => {
      if (cached) {
        return of(toHttpResponse(req, cached));
      }

      return throwError(() => error);
    }),
  );
}

function readCache(cache: ApiCacheService, key: string): Observable<ApiCacheEntry | null> {
  const memoryHit = cache.peek(key);
  if (memoryHit) {
    return of(memoryHit);
  }

  return from(cache.get(key));
}

function toHttpResponse(req: HttpRequest<unknown>, entry: ApiCacheEntry): HttpResponse<unknown> {
  return new HttpResponse({
    body: entry.body,
    status: entry.status,
    statusText: entry.statusText,
    url: req.urlWithParams,
  });
}

function cacheUserKey(auth: AuthService): string {
  return auth.getUser()?.user_id ?? auth.getPhone() ?? 'anon';
}
